import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { formatDisplayDate } from 'i18n/calendarLabels';
import routes from 'routes';
import { getAuthUser } from 'store/auth/selectors';
import { fetchUserHistoryRequest } from 'store/user/actions';
import { getUserHistory, getUserLoading } from 'store/user/selectors';
import { LoginDetails } from 'store/user/models';
import { PwaIcon } from './Icon';
import { PwaChip, PwaEmpty, PwaSection, PwaTopBar } from './ui';

type HistoryGroup = {
  group: string;
  items: LoginDetails[];
};

function groupHistory(
  history: LoginDetails[] | undefined,
  language: string,
  t: (key: string, opts?: Record<string, unknown>) => string
): HistoryGroup[] {
  if (!history?.length) return [];
  const sorted = [...history].sort(
    (a, b) =>
      new Date(b.loginTimestamp).getTime() - new Date(a.loginTimestamp).getTime()
  );
  const map = new Map<string, LoginDetails[]>();
  for (const item of sorted) {
    const key =
      formatDisplayDate(item.loginTimestamp, 'D MMM YYYY', language) ||
      t('common.unknown', { defaultValue: 'Unknown' });
    const list = map.get(key) || [];
    list.push(item);
    map.set(key, list);
  }
  return Array.from(map.entries()).map(([group, items]) => ({ group, items }));
}

export const PwaLoginHistoryPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(getAuthUser);
  const history = useSelector(getUserHistory);
  const loading = useSelector(getUserLoading);

  React.useEffect(() => {
    if (user?.userUuid) dispatch(fetchUserHistoryRequest(user.userUuid));
  }, [dispatch, user?.userUuid]);

  const groups = React.useMemo(
    () => groupHistory(history, i18n.language, t),
    [history, i18n.language, t]
  );

  const goBack = () => navigate(routes.account.root);

  return (
    <div className="pwa-more-page" data-shell="pwa">
      <PwaTopBar
        title={t('users.login-history', { defaultValue: 'Login history' })}
        onBack={goBack}
        backLabel={t('common.back')}
      />
      <main className="pwa-main">
        <div style={{ padding: '16px 0 4px' }}>
          <div className="pwa-banner warn" style={{ padding: 12 }}>
            <PwaIcon name="info" size={20} />
            <p style={{ margin: 0, fontSize: 'var(--gt-fs-sm, 13px)' }}>
              {t('users.login-history-warn', {
                defaultValue:
                  "Don't recognise a sign-in? Change your password and contact your administrator.",
              })}
            </p>
          </div>
        </div>
        {!loading && !groups.length ? (
          <PwaSection>
            <PwaEmpty
              title={t('users.no-login-history', {
                defaultValue: 'No login history',
              })}
              icon="history"
            />
          </PwaSection>
        ) : null}
        {groups.map((g) => (
          <PwaSection key={g.group} title={g.group}>
            <div className="pwa-list">
              {g.items.map((it, i) => {
                const isCurrent = i === 0 && g === groups[0];
                const at = formatDisplayDate(
                  it.loginTimestamp,
                  'D MMM YYYY, HH:mm',
                  i18n.language
                );
                return (
                  <div key={`${it.loginIP}-${String(it.loginTimestamp)}-${i}`} className="pwa-row static">
                    <span className="pwa-row-ico neutral" aria-hidden>
                      <PwaIcon name="globe" size={20} />
                    </span>
                    <span className="pwa-row-body">
                      <span className="pwa-row-title pwa-mono" style={{ fontWeight: 600 }}>
                        {it.loginIP || '—'}
                      </span>
                      <span className="pwa-row-sub">{at}</span>
                    </span>
                    {isCurrent ? (
                      <PwaChip tone="ok">
                        {t('users.this-device', { defaultValue: 'This device' })}
                      </PwaChip>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </PwaSection>
        ))}
      </main>
    </div>
  );
};

export default PwaLoginHistoryPage;
